/**
 * Capture: what this installation can accept, and how a phone joins.
 *
 * No preference in here, and that is the finding rather than an omission.
 * Every capture behaviour worth a control is decided per project or per
 * session - which subject, which purpose, which camera - and a global default
 * for any of them would be a setting that the next screen overrides. The
 * category exists because the *facts* are worth one place: whether pairing is
 * configured on this server, and how many formats it accepts.
 *
 * The button navigates rather than pairing. A pairing authorises capture for
 * one project, so minting one from a global screen would produce a credential
 * bound to nothing.
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getPairingAvailability } from "@/api/client";
import { IconPhone } from "@/components/icons";
import { Section } from "@/components/product";
import { Button, Callout } from "@/components/ui";
import { PAIRING_TERMS } from "@/i18n/terms";
import { useAsync } from "@/lib/useAsync";
import { useMediaCapabilities } from "@/lib/useMediaCapabilities";
import { paths } from "@/app/paths";
import { Fact } from "./Fact";

/** How many formats this installation accepts, counted from the server. */
function SupportedFormatCount() {
  const capabilities = useMediaCapabilities();
  if (!capabilities.loaded) {
    return (
      <Fact
        label="Desteklenen dosyalar"
        value="Okunuyor…"
      />
    );
  }
  return (
    <Fact
      label="Desteklenen dosyalar"
      value={`${capabilities.photos.length} fotoğraf ve ${capabilities.videos.length} video biçimi · tam liste içe aktarma ekranında`}
    />
  );
}

export function CapturePanel() {
  const navigate = useNavigate();
  const fetcher = useCallback(
    (signal: AbortSignal) => getPairingAvailability({ signal }),
    [],
  );
  const { state } = useAsync(fetcher);
  const availability = state.status === "success" ? state.data : null;

  return (
    <>
      <Section title="Telefon Bağlantısı">
        <p className="muted">{PAIRING_TERMS.sectionBody}</p>

        <div className="settings__device-state">
          <IconPhone size={20} />
          <div>
            <p className="settings__device-title">
              {availability === null
                ? "Durum okunuyor…"
                : availability.enabled && availability.configured
                  ? "Telefon eşleştirme kullanılabilir."
                  : PAIRING_TERMS.notConfiguredTitle}
            </p>
            <p className="muted small">{PAIRING_TERMS.sameNetwork}</p>
          </div>
        </div>

        <SupportedFormatCount />

        <div className="settings__actions">
          <Button variant="primary" onClick={() => navigate(paths.projects())}>
            {"Projelerime Git"}
          </Button>
        </div>
        <p className="muted small settings__hint">
          {"Telefon bağlantısı, bir projenin Çekimler ekranından başlatılır."}
        </p>

        {/*
          The one condition on phone capture, stated as a condition rather than
          as a release note. This screen used to say live camera capture would
          arrive "in the next phase" - true when pairing shipped, false the
          moment the capture page did, and false in the direction that costs
          someone a feature they already have. Browsers only expose a camera in
          a secure context, so the accurate sentence is about the address.
        */}
        <Callout tone="neutral">{"Telefon kamerası yalnızca güvenli (HTTPS) bir adreste açılabilir. SentyCap'e HTTPS üzerinden bağlıysanız telefon kamerası çalışır; yerel ağ adresiyle bağlandıysanız tarayıcı kamera erişimini vermez."}</Callout>
      </Section>

      <Section title="Eşleştirilmiş cihazlar">
        {/*
          The approved mockup lists a remembered iPhone here, with a last-seen
          timestamp and an auto-reconnect switch. None of that exists and none
          of it should: pairing was built without permanent device trust, so
          those controls would be either placebo or a real weakening of the
          design. The composition is kept and the content states the guarantee.
        */}
        <p className="muted">{"SentyCap kalıcı olarak eşleştirilmiş cihaz saklamaz. Her bağlantı tek kullanımlıktır ve oturum bittiğinde sona erer; otomatik yeniden bağlanma yoktur."}</p>
      </Section>
    </>
  );
}
